import React from "react";
import { connect } from "react-redux";
import _ from "lodash";
import Loader from "Components/loader/Loader";
import * as sessionActions from "../../modules/session";
import * as orderActions from "../../modules/orders";
import Stepper from "Components/stepper2/Stepper";
import GeoArea from "./add_form/GeoArea";
import CustomerType from "./add_form/CustomerType";
import PersonalData from "./add_form/PersonalData";
import OrderDetails from "./add_form/OrderDetails";
import Congratulations from "./add_form/Congratulations";
import BillingSystem from "./add_form/BillingSystem";
import Recap from "./add_form/Recap";
import PropTypes from "prop-types";
import * as resActions from "../../modules/resources";
import withFormState from "Components/withFormState";
import QboxmailApi from "../../qboxmailApi";
import t from "Utils/translation";
import { alertError } from "Utils/alerts";
import { formatServerErrors } from "Utils/serverFormats";
import currency from "currency.js";

const MIN_MAILBOXES_NO = 5;

// use currency.js to have an "EURO" function for all the operations on prices
const EURO = (value) => currency(value, { symbol: "€", decimal: ",", separator: "." });
const ContextTypes = {
  router: PropTypes.object,
  getLoggedUser: PropTypes.func,
};

const formInitialState = {
  country: "IT",
  customer_type: "private",
  billing_cycle: "monthly",
  payment_method: "stripe",
  phonenumber: "",
};

const COMMON_STEPS = {
  1: {
    component: <GeoArea />,
  },
  2: {
    component: <CustomerType />,
  },
  3: {
    component: <PersonalData />,
  },
  4: {
    component: <OrderDetails />,
  },
};

const ANNUAL_STEPS = {
  5: {
    component: <BillingSystem />,
  },
  6: {
    component: <Recap />,
  },
};

const MONTHLY_STEPS = {
  5: {
    component: <Recap />,
  },
};

class OrderNewScreen extends React.Component {
  constructor(props, context) {
    super(props, context);

    if (!IS_APP) {
      props.history.replace("/orders/new#1");
    }

    if (context.getLoggedUser()?.orders?.[0]) {
      props.history.replace("/orders");
    }

    this.state = {
      currentStep: 1,
      steps: {
        ...COMMON_STEPS,
        ...MONTHLY_STEPS,
      },
      orderCreated: false,
      estimate: 0,
      discountValue: 0,
      discountRecurrence: false,
      domains: [],
      loadingInvoiceUrl: false,
      emailCounters: {
        basic: {},
        professional: {},
        enterprise: {},
        archive: 0,
        total: 0,
      },
    };
  }

  showWHMCSInvoice = async () => {
    const now = Date.now();
    const isInvoiceUrlExpired = now - this.state.generatedInvoiceUrl > 40000;

    if (this.state.invoiceUrl && !isInvoiceUrlExpired) {
      // if we have just received the token from the server
      // we use it without generating a new one
      window.open(this.state.invoiceUrl, "_blank");
      this.setState({ generatedInvoiceUrl: null, invoiceUrl: null });
    } else {
      // request the token again to have it fresh
      this.setState({ loadingInvoiceUrl: true });
      const api = new QboxmailApi({ useCookie: true });
      try {
        const response = await api.generateWHMCSUrl("show_invoice");
        window.open(response.resources[0].url, "_blank");
      } catch (error) {
        console.log("error generating whmcs invoice", error);
      }

      this.setState({ loadingInvoiceUrl: false });
    }
  };

  UNSAFE_componentWillMount() {
    this.props.refreshLoggedUserData();
    this.props.getOrders();
  }

  componentDidMount() {
    const customer = this.context.getLoggedUser();

    this.props.setFormFieldValue({
      max_emails_no_8gb: customer.email_accounts_8gb,
      max_emails_no_25gb: customer.email_accounts_25gb,
      max_emails_no_archive: customer.email_accounts_archive,
      email_address: customer.email_address,
    });

    if (customer.declared_type) {
      this.props.setFormFieldValue({
        customer_type: customer.declared_type,
        billing_cycle: customer.declared_type === "reseller" ? "monthly" : "annually",
      });
    }

    Promise.all([this.getResourcesCounters(), this.getPrices()]).then(() => {
      const eur = this.calcMonthlyEstimate();
      this.setState({ estimate: eur });
    });
  }

  calcMonthlyEstimate = () => {
    const { prices, emailCounters } = this.state;
    const { total } = emailCounters;

    if (!prices) return;

    let estimate = currency(0);

    for (let plan of ["basic", "professional", "enterprise"]) {
      for (let size in emailCounters[plan]) {
        if (emailCounters[plan][size] > 0) {
          estimate = estimate.add(currency(prices["monthly"][`${plan}_${size}`]).multiply(emailCounters[plan][size]));
        }
      }
    }

    // add archive
    if (emailCounters.archive > 0) {
      estimate = estimate.add(currency(prices["monthly"]["basic_vault"]).multiply(emailCounters.archive));
    }

    // console.log("esitmate without min", estimate.format());

    if (total < MIN_MAILBOXES_NO) {
      const to_add = MIN_MAILBOXES_NO - total;
      // add to the toal X basic_8
      estimate = estimate.add(currency(prices["monthly"]["basic_8"]).multiply(to_add));
    }

    // console.log("esitmate with min", estimate.format());

    return estimate.format();
  };

  getPrices = async () => {
    const api = new QboxmailApi({ useCookie: true });
    const prices = await api.getPrices();

    this.setState({ prices: prices.resources[0] });

    return prices;
  };

  getResourcesCounters = async () => {
    const api = new QboxmailApi({ useCookie: true });
    const domains = await api.getDomains({
      per: 10000,
    });

    let emailCounters = { archive: 0, total: 0 };
    for (let d of domains.resources) {
      const gb8 =
        d.no_email_accounts - (d.no_email_accounts_25gb + d.no_email_accounts_50gb + d.no_email_accounts_100gb);

      if (!emailCounters[d.plan.name]) {
        emailCounters[d.plan.name] = {
          8: 0,
          25: 0,
          50: 0,
          100: 0,
        };
      }

      emailCounters[d.plan.name][8] += gb8;
      emailCounters[d.plan.name][25] += d.no_email_accounts_25gb;
      emailCounters[d.plan.name][50] += d.no_email_accounts_50gb;
      emailCounters[d.plan.name][100] += d.no_email_accounts_100gb;

      emailCounters.archive += d.no_email_accounts_archive;
      emailCounters.total += d.no_email_accounts;
    }

    this.setState({
      domains: domains.resources,
      emailCounters,
    });

    return domains;
  };

  componentDidUpdate(prevProps) {
    const { billing_cycle } = this.props.formData;
    const prevBillingCycle = prevProps.formData.billing_cycle;

    if (prevBillingCycle === billing_cycle) return;

    if (billing_cycle === "monthly") {
      this.setState({
        steps: {
          ...COMMON_STEPS,
          ...MONTHLY_STEPS,
        },
      });
    } else {
      this.setState({
        steps: {
          ...COMMON_STEPS,
          ...ANNUAL_STEPS,
        },
      });
    }
  }

  componentWillUnmount() {
    this.props.resetForm();
    this.props.resetOrdersState();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const loggedUser = this.context.getLoggedUser();
    if (!IS_APP) {
      // render the right step if the user click back in the browser
      const nextStep = nextProps.location.hash.replace("#", "");

      this.setState({ currentStep: nextStep || 1 });
    }

    if (!_.isEmpty(nextProps.order) && nextProps.order.status !== "paid") {
      this.setState({ orderCreated: true, orderCreatedCode: nextProps.order.code });
    }
  }

  updateEstimate = (value) => {
    this.setState({ estimate: value });
  };

  updateDiscountValue = (value, recurrence) => {
    this.setState({ discountValue: value, discountRecurrence: recurrence });
  };

  submitForm = (e) => {
    e.preventDefault();
    const { formData, setFormLoading, unsetFormLoading, setFormErrors } = this.props;

    setFormLoading();

    const api = new QboxmailApi({ useCookie: true });
    api
      .createOrder(formData)
      .then((data) => {
        unsetFormLoading();

        this.setState({
          orderCreated: true,
          orderCreatedCode: data.resource_code,
          invoiceUrl: data.invoice_url,
          generatedInvoiceUrl: Date.now(),
        });

        this.props.refreshLoggedUserData();
      })
      .catch((response) => {
        unsetFormLoading();
        formatServerErrors(response, true).then((errorString) => {
          alertError(t("alert.wrong.contactUs"));
          setFormErrors({ onForm: errorString });
          unsetFormLoading();
        });
      });
  };

  goToStep = (no) => {
    if (!IS_APP) {
      this.props.history.push("/orders/new#" + no);
    }

    window.scrollTo(0, 0);
    this.setState({ currentStep: no });
  };

  showStepNumber = (no) => {
    return this.state.steps[no];
  };

  backToList = () => {
    this.props.history.push("/orders/new#1");
  };

  prevStep = () => {
    if (this.state.currentStep <= 1) return;
    let prevStep = this.state.currentStep - 1;
    if (!IS_APP) {
      this.props.history.push("/orders/new#" + prevStep);
    }
    this.setState({ currentStep: prevStep });
  };

  render() {
    const { isOrderLoading } = this.props;
    let toShow = this.showStepNumber(this.state.currentStep);

    if (isOrderLoading) {
      return <Loader {...this.props} />;
    }

    const customer = this.context.getLoggedUser();
    return (
      <div className="contentWrapper">
        {this.state.orderCreated && (
          <Congratulations
            showWHMCSInvoice={this.showWHMCSInvoice}
            orderCode={this.state.orderCreatedCode}
            loadingInvoiceUrl={this.state.loadingInvoiceUrl}
            formData={this.props.formData}
          />
        )}
        {!this.state.orderCreated && (
          <div className="contentWrapper">
            {React.cloneElement(toShow.component, {
              ...this.props,
              domains: this.state.domains,
              verifyVAT: orderActions.verifyVAT,
              verifyFiscalcode: orderActions.verifyFiscalcode,
              verifyPromocode: orderActions.verifyPromocode,
              updateEstimate: this.updateEstimate,
              MIN_MAILBOXES_NO: MIN_MAILBOXES_NO,
              updateDiscountValue: this.updateDiscountValue,
              estimate: this.state.estimate,
              discountValue: this.state.discountValue,
              discountRecurrence: this.state.discountRecurrence,
              customer,
              goToStep: this.goToStep.bind(this),
              submitForm: this.submitForm.bind(this),
              backToList: this.backToList,
              emailCounters: this.state.emailCounters,
              EURO,
              prices: this.state.prices,
              stepper: (
                <Stepper
                  steps={Object.keys(this.state.steps).length}
                  active={parseInt(this.state.currentStep, 10)}
                  goBack={this.prevStep}
                />
              ),
            })}
          </div>
        )}
      </div>
    );
  }
}

OrderNewScreen.contextTypes = ContextTypes;

const mapStateToProps = (state) => ({
  orders: state.orders,
  order: resActions.getData("orders.data.0", state),
  isOrderLoading: resActions.isResourceLoading("orders", state),
});

const mapDispatchToProps = (dispatch) => ({
  getOrders: (queryParams) => dispatch(resActions.callApi("getOrders", "orders", queryParams)),
  refreshLoggedUserData: () => {
    dispatch(sessionActions.verify(false));
  },
  resetOrdersState: () => {
    dispatch(orderActions.resetOrdersState());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(withFormState(OrderNewScreen, formInitialState));
